import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { apiClient } from '../services/api';
import { User, ChangePasswordRequest, UserStats } from '../types/api';
import './Settings.css';

interface SettingsState {
  activeTab: 'profile' | 'password' | 'theme' | 'goals' | 'subscription' | 'account';
  user: User | null;
  stats: UserStats | null;
  isLoading: boolean;
  error: string | null;
  success: string | null;
}

interface ProfileFormData {
  full_name: string;
  bio: string;
  avatar_url: string;
  timezone: string;
}

interface PasswordFormData {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

interface WritingGoals {
  daily_word_count: number;
  weekly_word_count: number;
  target_completion_date: string;
  writing_streak_goal: number;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const [state, setState] = useState<SettingsState>({
    activeTab: 'profile',
    user: null,
    stats: null,
    isLoading: true,
    error: null,
    success: null
  });

  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    full_name: '',
    bio: '',
    avatar_url: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [writingGoals, setWritingGoals] = useState<WritingGoals>({
    daily_word_count: 500,
    weekly_word_count: 3500,
    target_completion_date: '',
    writing_streak_goal: 30
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const [userProfile, userStats] = await Promise.all([
        apiClient.getProfile(),
        apiClient.getUserStats()
      ]);

      setState(prev => ({
        ...prev,
        user: userProfile,
        stats: userStats,
        isLoading: false
      }));

      // Populate forms with user data
      setProfileForm({
        full_name: userProfile.full_name || '',
        bio: userProfile.bio || '',
        avatar_url: userProfile.avatar_url || '',
        timezone: userProfile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      if (userProfile.writing_goals) {
        setWritingGoals({
          daily_word_count: userProfile.writing_goals.daily_word_count || 500,
          weekly_word_count: userProfile.writing_goals.weekly_word_count || 3500,
          target_completion_date: userProfile.writing_goals.target_completion_date || '',
          writing_streak_goal: userProfile.writing_goals.writing_streak_goal || 30
        });
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to load user data',
        isLoading: false
      }));
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setState(prev => ({ ...prev, error: null, success: null }));

      const updatedUser = await apiClient.updateProfile({
        full_name: profileForm.full_name,
        bio: profileForm.bio,
        avatar_url: profileForm.avatar_url,
        timezone: profileForm.timezone
      });

      setState(prev => ({
        ...prev,
        user: updatedUser,
        success: 'Profile updated successfully'
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to update profile'
      }));
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setState(prev => ({ ...prev, error: 'New passwords do not match' }));
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setState(prev => ({ ...prev, error: 'New password must be at least 8 characters' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, error: null, success: null }));

      const data: ChangePasswordRequest = {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      };

      await apiClient.changePassword(data);

      setState(prev => ({
        ...prev,
        success: 'Password changed successfully'
      }));

      setPasswordForm({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to change password. Please check your current password.'
      }));
    }
  };

  const handleWritingGoalsUpdate = async () => {
    try {
      setState(prev => ({ ...prev, error: null, success: null }));

      await apiClient.updateProfile({
        writing_goals: writingGoals
      });

      setState(prev => ({
        ...prev,
        success: 'Writing goals updated successfully'
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to update writing goals'
      }));
    }
  };

  const handleAccountDelete = async () => {
    try {
      await apiClient.deleteAccount();
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to delete account'
      }));
    }
  };

  const handleManageSubscription = async () => {
    try {
      setState(prev => ({ ...prev, error: null, success: null, isLoading: true }));

      // Get Stripe billing portal URL
      const { url } = await apiClient.createStripePortalSession(
        `${window.location.origin}/settings?tab=subscription`
      );

      // Redirect to Stripe billing portal
      window.location.href = url;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to open billing portal',
        isLoading: false
      }));
    }
  };

  const clearMessages = () => {
    setState(prev => ({ ...prev, error: null, success: null }));
  };

  if (state.isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Settings</h1>
      </div>

      <div className="settings-container">
        <nav className="settings-nav">
          <button
            className={`nav-item ${state.activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'profile' }));
              clearMessages();
            }}
          >
            Profile
          </button>
          <button
            className={`nav-item ${state.activeTab === 'password' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'password' }));
              clearMessages();
            }}
          >
            Password
          </button>
          <button
            className={`nav-item ${state.activeTab === 'theme' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'theme' }));
              clearMessages();
            }}
          >
            Theme
          </button>
          <button
            className={`nav-item ${state.activeTab === 'goals' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'goals' }));
              clearMessages();
            }}
          >
            Writing Goals
          </button>
          <button
            className={`nav-item ${state.activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'subscription' }));
              clearMessages();
            }}
          >
            Subscription
          </button>
          <button
            className={`nav-item ${state.activeTab === 'account' ? 'active' : ''}`}
            onClick={() => {
              setState(prev => ({ ...prev, activeTab: 'account' }));
              clearMessages();
            }}
          >
            Account
          </button>
        </nav>

        <main className="settings-content">
          {state.error && (
            <div className="message error">
              {state.error}
              <button onClick={clearMessages}>×</button>
            </div>
          )}

          {state.success && (
            <div className="message success">
              {state.success}
              <button onClick={clearMessages}>×</button>
            </div>
          )}

          {state.activeTab === 'profile' && (
            <section className="settings-section">
              <h2>Profile Information</h2>
              <form onSubmit={handleProfileUpdate} className="profile-form">

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={state.user?.email || ''}
                    disabled
                    className="disabled"
                  />
                  <small>Email cannot be changed</small>
                </div>

                <div className="form-group">
                  <label htmlFor="full_name">Full Name</label>
                  <input
                    type="text"
                    id="full_name"
                    value={profileForm.full_name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="avatar_url">Avatar URL</label>
                  <input
                    type="url"
                    id="avatar_url"
                    value={profileForm.avatar_url}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, avatar_url: e.target.value }))}
                    placeholder="https://example.com/avatar.jpg"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    value={profileForm.timezone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, timezone: e.target.value }))}
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                    <option value="Asia/Tokyo">Tokyo</option>
                    <option value="Australia/Sydney">Sydney</option>
                  </select>
                </div>

                <button type="submit" className="btn-primary">
                  Update Profile
                </button>
              </form>
            </section>
          )}

          {state.activeTab === 'password' && (
            <section className="settings-section">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label htmlFor="old_password">Current Password</label>
                  <input
                    type="password"
                    id="old_password"
                    value={passwordForm.old_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, old_password: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="new_password">New Password</label>
                  <input
                    type="password"
                    id="new_password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    required
                    minLength={8}
                  />
                  <small>Password must be at least 8 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirm_password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    required
                  />
                </div>

                <button type="submit" className="btn-primary">
                  Change Password
                </button>
              </form>
            </section>
          )}

          {state.activeTab === 'theme' && (
            <section className="settings-section">
              <h2>Theme Preferences</h2>
              <div className="theme-options">
                <div className="theme-option">
                  <label>
                    <input
                      type="radio"
                      name="theme"
                      value="light"
                      checked={theme === 'light'}
                      onChange={() => setTheme('light')}
                    />
                    <div className="theme-preview light">
                      <div className="preview-header"></div>
                      <div className="preview-content"></div>
                    </div>
                    <span>Light Theme</span>
                  </label>
                </div>

                <div className="theme-option">
                  <label>
                    <input
                      type="radio"
                      name="theme"
                      value="dark"
                      checked={theme === 'dark'}
                      onChange={() => setTheme('dark')}
                    />
                    <div className="theme-preview dark">
                      <div className="preview-header"></div>
                      <div className="preview-content"></div>
                    </div>
                    <span>Dark Theme</span>
                  </label>
                </div>
              </div>
            </section>
          )}

          {state.activeTab === 'goals' && (
            <section className="settings-section">
              <h2>Writing Goals</h2>

              {state.stats && (
                <div className="stats-overview">
                  <h3>Your Writing Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-number">{state.stats?.memoir_count || 0}</span>
                      <span className="stat-label">Memoirs</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{(state.stats?.total_word_count || 0).toLocaleString()}</span>
                      <span className="stat-label">Total Words</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{state.stats?.chapters_written || 0}</span>
                      <span className="stat-label">Chapters</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{state.stats?.days_active || 0}</span>
                      <span className="stat-label">Days Active</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="goals-form">
                <div className="form-group">
                  <label htmlFor="daily_word_count">Daily Word Count Goal</label>
                  <input
                    type="number"
                    id="daily_word_count"
                    value={writingGoals.daily_word_count}
                    onChange={(e) => setWritingGoals(prev => ({ ...prev, daily_word_count: parseInt(e.target.value) || 0 }))}
                    min="0"
                    step="50"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="weekly_word_count">Weekly Word Count Goal</label>
                  <input
                    type="number"
                    id="weekly_word_count"
                    value={writingGoals.weekly_word_count}
                    onChange={(e) => setWritingGoals(prev => ({ ...prev, weekly_word_count: parseInt(e.target.value) || 0 }))}
                    min="0"
                    step="100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="target_completion_date">Target Completion Date</label>
                  <input
                    type="date"
                    id="target_completion_date"
                    value={writingGoals.target_completion_date}
                    onChange={(e) => setWritingGoals(prev => ({ ...prev, target_completion_date: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="writing_streak_goal">Writing Streak Goal (days)</label>
                  <input
                    type="number"
                    id="writing_streak_goal"
                    value={writingGoals.writing_streak_goal}
                    onChange={(e) => setWritingGoals(prev => ({ ...prev, writing_streak_goal: parseInt(e.target.value) || 0 }))}
                    min="1"
                  />
                </div>

                <button type="button" onClick={handleWritingGoalsUpdate} className="btn-primary">
                  Update Goals
                </button>
              </div>
            </section>
          )}

          {state.activeTab === 'subscription' && (
            <section className="settings-section">
              <h2>Subscription & Billing</h2>

              <div className="subscription-info">
                <h3>Current Subscription</h3>
                <div className="subscription-card">
                  <div className="subscription-header">
                    <div>
                      <h4>{state.user?.subscription_tier ? state.user.subscription_tier.charAt(0).toUpperCase() + state.user.subscription_tier.slice(1) : 'Free'} Plan</h4>
                      <p className="subscription-status">
                        Status: <span className={`status-badge ${state.user?.subscription_status}`}>
                          {state.user?.subscription_status || 'Active'}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="subscription-details">
                    {state.user?.subscription_expires_at && (
                      <div className="detail-row expiry-warning">
                        <span className="detail-label">⚠️ Subscription Ends:</span>
                        <span className="detail-value expiry-date">
                          {new Date(state.user.subscription_expires_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Chapters:</span>
                      <span className="detail-value">
                        {state.user?.chapter_limit === -1 ? 'Unlimited' : state.user?.chapter_limit}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">AI Requests/Month:</span>
                      <span className="detail-value">
                        {state.user?.ai_usage_limit === -1 ? 'Unlimited' : state.user?.ai_usage_limit?.toLocaleString()}
                      </span>
                    </div>
                    {state.user?.addon_subscriptions && Object.keys(state.user.addon_subscriptions).length > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Add-ons:</span>
                        <div className="addon-list">
                          {Object.values(state.user.addon_subscriptions).map((addon: any, idx) => (
                            <div key={idx} className="addon-item">
                              <div className="addon-info">
                                <span className="addon-name">{addon.plan_name}</span>
                                {addon.status === 'canceling' && addon.expires_at && (
                                  <span className="addon-status canceling">
                                    Expires {new Date(addon.expires_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </span>
                                )}
                                {(!addon.status || addon.status === 'active') && (
                                  <span className="addon-status active">Active</span>
                                )}
                              </div>
                              {addon.ai_usage_limit && addon.ai_usage_limit > 0 && (
                                <span className="addon-limit">+{addon.ai_usage_limit.toLocaleString()} AI requests</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="subscription-actions">
                    {state.user?.subscription_tier !== 'free' && state.user?.subscription_tier !== 'unverified' ? (
                      <button
                        className="btn-primary"
                        onClick={handleManageSubscription}
                      >
                        Manage Subscription
                      </button>
                    ) : (
                      <button
                        className="btn-primary"
                        onClick={() => navigate('/subscription')}
                      >
                        Upgrade Plan
                      </button>
                    )}
                    <button
                      className="btn-secondary"
                      onClick={() => navigate('/subscription')}
                    >
                      View All Plans
                    </button>
                  </div>
                </div>

                <div className="subscription-note">
                  <p><strong>Manage Subscription</strong> opens the Stripe billing portal where you can:</p>
                  <ul>
                    <li>Update your payment method</li>
                    <li>View invoices and payment history</li>
                    <li>Cancel or change your subscription</li>
                    <li>Manage add-on subscriptions</li>
                  </ul>
                  <p className="warning-text">
                    ⚠️ If you cancel your paid subscription, you'll be downgraded to the Free tier and will lose access to editing your memoirs (read-only access will remain).
                  </p>
                </div>
              </div>
            </section>
          )}

          {state.activeTab === 'account' && (
            <section className="settings-section">
              <h2>Account Management</h2>

              <div className="account-info">
                <h3>Account Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Account Status:</span>
                    <span className={`info-value ${state.user?.is_active ? 'active' : 'inactive'}`}>
                      {state.user?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email Verified:</span>
                    <span className={`info-value ${state.user?.is_verified ? 'verified' : 'unverified'}`}>
                      {state.user?.is_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Subscription:</span>
                    <span className="info-value subscription">
                      {state.user?.subscription_tier || 'Free'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Member Since:</span>
                    <span className="info-value">
                      {state.user?.created_at ? new Date(state.user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="danger-zone">
                <h3>Danger Zone</h3>
                <div className="danger-actions">
                  <div className="danger-item">
                    <div className="danger-info">
                      <h4>Delete Account</h4>
                      <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
                    </div>
                    <button
                      className="btn-danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

              {showDeleteConfirm && (
                <div className="delete-confirm-modal">
                  <div className="modal-content">
                    <h3>Confirm Account Deletion</h3>
                    <p>
                      Are you absolutely sure you want to delete your account? This will permanently delete:
                    </p>
                    <ul>
                      <li>All your memoirs and chapters</li>
                      <li>All notes and todos</li>
                      <li>Your profile and settings</li>
                      <li>All version history</li>
                    </ul>
                    <p><strong>This action cannot be undone.</strong></p>

                    <div className="modal-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-danger"
                        onClick={handleAccountDelete}
                      >
                        Yes, Delete My Account
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default Settings;